'use client'

import {observer} from 'mobx-react'
import React, {useEffect, useState} from 'react'

import {Product} from '@/src/core/domain/entities/product'
import {PaginationContainer} from '@/src/features/common/pagination'

import {useStore} from '@/src/shared/hooks/useStore'
import {Label} from '@/src/shared/ui/label'
import {LoadingPage} from '@/src/shared/ui/layout'
import {ProductsListContainer} from '@/src/features/catalog/product-list/products-list-container'
import {getInjection} from '@runlab/di'
import {FavouriteEventTypes} from '@/src/core/domain/events'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {getClient} from '@/src/shared/utils/api-factory'
import {Routes} from '@/src/config/routes'
import {PageData} from '@/src/core/domain/entities/page/page-data'
import {useSearchParams} from 'next/navigation'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'
import {isWeb} from '@/src/shared/utils/functions'

export type FavouritePageContent = {
  products: {
    items: Product[]
    page: number
    limit: number
    pages: number
    total: number
  }
}

export const FavouritePageScreen: React.FC<PageData<FavouritePageContent>> = () => {
  return <FavouritePageContentComponent />
}

export const FavouritePageContentComponent = observer(() => {
  const searchParams = useSearchParams()
  const store = useStore<FavouritePageContent>()
  const [products, setProducts] = useState<Product[]>([])
  const queryClient = useQueryClient()

  const queryKey = store.queryKey

  // eslint-disable-next-line
  const query = useQuery<PageData<FavouritePageContent>>({
    enabled: isWeb(),
    queryKey,
    queryFn: async () => {
      return getClient('api').post(Routes.api.favourite, {
        data: {
          type: store.pageData?.type,
          parameters: {
            productIds: await getInjection('FavouriteRepositoryInterface').getAll(),
            page: searchParams.get('page'),
          },
        },
      })
    },
  })

  useEffect(() => {
    const eventBus = getInjection('IEventBus')

    eventBus.subscribe(FavouriteEventTypes.ProductRemoved, () => {
      queryClient.invalidateQueries({queryKey})
    })
  }, [])

  useEffect(() => {
    if (query.data && !query.data.suspendUntilClientReady) {
      store.setPageData(query.data, queryKey)
      setProducts(query.data.content.products.items)
    }
  }, [query.data])

  useEffect(() => {
    if (!isWeb() && store.pageData && !query.data?.suspendUntilClientReady) {
      setProducts(store.pageData.content.products.items)
    }
  }, [store.pageData])

  if (!store.pageData?.type || query.data?.suspendUntilClientReady) return <LoadingPage />
  if (!store.pageData?.content.products) return <LoadingPage />

  const pageData = store.pageData

  return (
    <>
      {pageData.meta?.title && <title>{pageData.meta?.title}</title>}
      {pageData.meta?.description && (
        <meta name="description" content={pageData.meta?.description} />
      )}

      {((query.isLoading || query.isFetching) && <AsyncProcessLoading />) ||
        (products.length > 0 ? (
          <>
            {pageData.content.products.pages > 1 && (
              <PaginationContainer
                showLimit={false}
                page={pageData.content.products.page}
                limit={pageData.content.products.limit}
                total={pageData.content.products.total}
              />
            )}

            <ProductsListContainer products={products} />

            {pageData.content.products.pages > 1 && (
              <PaginationContainer
                showLimit={false}
                page={pageData.content.products.page}
                limit={pageData.content.products.limit}
                total={pageData.content.products.total}
              />
            )}
          </>
        ) : (
          <Label>Список избранных товаров пуст</Label>
        ))}
    </>
  )
})
